import * as i0 from '@angular/core';
import { InjectionToken, Injectable, Inject, NgModule } from '@angular/core';
import * as i1 from '@angular/router';
import { ActivationEnd, RouterModule } from '@angular/router';
import * as i2 from '@usana/ux/markets';
import { UsanaLoggingService } from '@usana/ux/common/logging';
import { CommonModule } from '@angular/common';

class ResolveWrapper {
    constructor(param, resolve) {
        this.param = param;
        this.resolve = resolve;
    }
}

// Market base route, default is empty
const MARKET_BASE_ROUTE = new InjectionToken('market.route.base', {
    factory: () => ['../'],
});

class MarketResolver {
    constructor(router, marketService, marketBaseRoute) {
        this.router = router;
        this.marketService = marketService;
        this.marketBaseRoute = marketBaseRoute;
        this.broadLocaleMatchPattern = /^[a-zA-Z]{2,3}[-._][a-zA-Z]{2}$/;
        this.alwaysExcludedPaths = [':marketId', ':locale'].concat(marketBaseRoute);
        // build a dummy market object
        this.currentMarket = {
            currentMarketId: undefined,
        };
        this.savedPathParam = [];
        this.firstResolve = true;
        this.router.events.subscribe((val) => {
            if (val instanceof ActivationEnd) {
                this.currentRouteSnapshot = val.snapshot;
                this.savedPathParam = []; // we have completed navigation so we don't need to hold onto this path anymore
            }
        });
        /**
         * there are a few scenarios here
         * 1. if we load without a current market id, that means we have preloaded markets so don't do anything
         * 2. if our loaded market id is different than what we are currently using, something (backend/front end)
         *    requested a market change or informed us of our market for the first tiem
         *    a) if we had a value before we need to update the url to reflect that
         *    b) if we didn't have a value before we won't do anything YET
         *
         * additionally we will resolve any outstanding promise that is blocking the angular resolve
         * 1. if the requested parameter differs from what we ended up loading, then lets update the url to reflect reality
         * resolve the outstanding promise
         */
        this.marketService.geti18NInfoSubject().subscribe((i18nInfo) => {
            if (!!i18nInfo.current.currentMarketId) {
                if (i18nInfo.current.currentMarketId !== this.currentMarket.currentMarketId) {
                    const hadBefore = !!this.currentMarket.currentMarketId;
                    this.currentMarket = i18nInfo.current;
                    if (hadBefore && !this.currentResolve) {
                        // update the url to reflect the fact that our market is now different
                        this.updateUrl(this.savedPathParam);
                    }
                }
                if (this.currentResolve) {
                    // something is waiting
                    if (this.currentResolve.param !== this.currentMarket.currentMarketId) {
                        const forcedPath = [];
                        if (!this.seemsToBeMarketId(this.currentResolve.param)) {
                            // this isn't any locale format we recognize, so lets assume its a path
                            forcedPath.push(this.currentResolve.param);
                        }
                        this.updateUrl(forcedPath);
                    }
                    // always resolve even if we redirect to make sure we don't have any processes hanging
                    this.resolveAndCleanup();
                }
            }
        });
    }
    /**
     * resolve the market based on the route
     * there are several scenarios in this case
     * 1. the requested param is the same as our current market, so just resolve that
     * 2. the param is corrected to a market we understand, so resolve that
     * 3. the param seems to be of the right format for a locale but we don't have a record for it
     *    a) if we have preloaded our markets its possible a valid market was requested but it wasn't preloaded, wait for
     *        the subscription to inform us what locale we are
     *    b) we have finished the backload of the markets and it doesn't match so make a hard request to the server to
     *        determine what locale to use
     * 4. we have requested a url like app/page instead of app/market/page
     *    a) if we haven't finished the backload let the subscription handle this
     *    b) if we have finished the backload use whatever the current market is and reload the market/page where page is
     *        what we requested originally
     * @param route
     * @param state
     */
    resolve(route, state) {
        const param = route.paramMap.get('marketId') || route.paramMap.get('locale');
        // route.params.marketId;
        // this needs to be done because we don't update our route snapshot until we end activation
        this.currentRouteSnapshot = route;
        return new Promise((resolve) => {
            if (this.currentMarket.currentMarketId === param) {
                // if we are the same as what we already have no reason to do anything
                resolve(this.currentMarket);
            }
            else {
                // some sort of market check/load will need to complete so lets store the resolve
                // and what we would prefer
                this.currentResolve = new ResolveWrapper(param, resolve);
                // if we have a value but it didn't match the request that means the url was manually changed
                // lets try to correct it (check against standard form + any odsy/portal/phx forms we have loaded)
                this.marketService.correctedMarketId(param, false).then((foundMarketId) => {
                    if (foundMarketId) {
                        // cool we can just set the market
                        if (foundMarketId === this.currentMarket.currentMarketId) {
                            this.updateUrl([]); // need to make sure the better marketId is present in the url
                            this.resolveAndCleanup();
                        }
                        else {
                            this.marketService.setMarketId(foundMarketId);
                        }
                    }
                    else if (this.seemsToBeMarketId(param)) {
                        if (this.currentMarket.currentMarketId) {
                            // we need to force a lookup of our current market from the backend.
                            this.marketService.forceReloadOfCurrentFromBackend();
                        }
                        // else the sub will handle this logic
                    }
                    else if (this.currentMarket.currentMarketId && !!this.currentResolve) {
                        // we are going to use our previous market to resolve our current request
                        this.savedPathParam = [param];
                        // set to false with DEV-66823 going from enu-US/login to -> /forgot was replacing login in the history
                        // set to true (only for the first resolve) with DEV-73076 fix issues with going to /login not replacing the history [/login, enu-US/login] both in the history
                        this.updateUrl(this.savedPathParam, this.firstResolve);
                        // always resolve even if we redirect to make sure we don't have any processes hanging
                        this.resolveAndCleanup();
                    }
                });
                // else this will resolve with the market subscription is handled
            }
        });
    }
    /**
     * resolve the current Resolve promise and then delete it
     */
    resolveAndCleanup() {
        this.currentResolve.resolve(this.currentMarket);
        delete this.currentResolve;
    }
    /**
     * update the url as needed
     * @param forcedPath a portion of the path to force into the path
     * @param replaceUrl whether or not we should user a router navigate replaceUrl option
     * useful in the case where #/:marketId is instead referring to a page #/lifestyle
     */
    updateUrl(forcedPath, replaceUrl = true) {
        let base = this.marketBaseRoute.concat([this.currentMarket.currentMarketId]);
        base = base.concat(forcedPath).concat(this.currentChildPath());
        UsanaLoggingService.logMsg(`market resolver updating url to ${base}, ${replaceUrl}`);
        this.firstResolve = false;
        this.router.navigate(base, { queryParams: this.currentRouteSnapshot.queryParams, replaceUrl: replaceUrl });
    }
    /**
     * build the current path based on our route snapshot
     */
    currentChildPath() {
        // exclude our route from the path so it isn't caught immediately
        return this.buildChildPaths(this.currentRouteSnapshot, this.alwaysExcludedPaths);
    }
    /**
     * recursively append child routes together to build a full route
     * @param route the current route
     * @param excludedPaths paths to exclude at the current level
     * :marketId and :locale if at the top (since our first level is the resolver)
     * or redirectsTo paths at lower levels
     */
    buildChildPaths(route, excludedPaths) {
        // if we have no url or we are the root route (parent is no config)
        if (!!route && (route.url.length > 0 || (route.parent && route.parent.routeConfig === null))) {
            // build excluded child paths
            let excludedChildPaths = [];
            if (!!route.routeConfig && !!route.routeConfig.children) {
                // find any with a redirect and exclude them, we aren't playing with redirects since they mess us up.
                excludedChildPaths = route.routeConfig.children.filter((r) => r.redirectTo).map((r) => r.redirectTo);
            }
            excludedChildPaths = excludedChildPaths.concat(this.alwaysExcludedPaths);
            if (excludedPaths.indexOf(route.routeConfig.path) >= 0) {
                return [].concat(this.buildChildPaths(route.children[0], excludedChildPaths));
            }
            else {
                return route.url.map((seg) => seg.path).concat(this.buildChildPaths(route.children[0], excludedChildPaths));
            }
        }
        return [];
    }
    /**
     * does the string match our loose regex test for markets
     * @param toTest the string to test
     */
    seemsToBeMarketId(toTest) {
        return !!toTest && this.broadLocaleMatchPattern.test(toTest);
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: MarketResolver, deps: [{ token: i1.Router }, { token: i2.UsanaMarketService }, { token: MARKET_BASE_ROUTE }], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: MarketResolver }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: MarketResolver, decorators: [{
            type: Injectable
        }], ctorParameters: function () { return [{ type: i1.Router }, { type: i2.UsanaMarketService }, { type: undefined, decorators: [{
                    type: Inject,
                    args: [MARKET_BASE_ROUTE]
                }] }]; } });

class UsanaMarketsRoutingModule {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: UsanaMarketsRoutingModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "16.2.12", ngImport: i0, type: UsanaMarketsRoutingModule, imports: [CommonModule, RouterModule] }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: UsanaMarketsRoutingModule, providers: [MarketResolver], imports: [CommonModule, RouterModule] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: UsanaMarketsRoutingModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [CommonModule, RouterModule],
                    providers: [MarketResolver],
                }]
        }] });

/**
 * Generated bundle index. Do not edit.
 */

export { MARKET_BASE_ROUTE, MarketResolver, UsanaMarketsRoutingModule };
